import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PostSearchResult } from '../../types/search';
import { formatDistanceToNow } from 'date-fns';
import { FramedAvatar } from '../ui/FramedAvatar';
import { LikeButton } from '../social/LikeButton';
import { CommentButton } from '../social/CommentButton';
import { CommentInput } from '../social/CommentInput';
import { CommentItem } from '../social/CommentItem';
import { PendingCommentItem } from '../social/PendingCommentItem';
import { usePostComments } from '../../hooks/usePostComments';
import { Button } from '../ui/button';
import { Loader2, MoreHorizontal, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { CustomDropdownMenu } from '../ui/CustomDropdownMenu';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '../ui/alert-dialog';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../ui/use-toast';

interface PostSearchCardProps {
  post: PostSearchResult;
  searchState?: {
    query: string;
    activeTab: string;
    results: any[];
  };
}

// Função utilitária para formato curto de tempo
function formatShortDistance(date: Date | number): string {
  const now = new Date();
  const diff = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo`;
  return `${Math.floor(diff / 31536000)}y`;
}

export const PostSearchCard: React.FC<PostSearchCardProps> = ({ post, searchState }) => {
  const { user } = useAuth();
  const isOwnPost = user?.id === post.author.id;
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    comments,
    commentCount,
    isLoading,
    hasMore,
    pendingComments,
    loadMoreComments,
    addComment,
    retryComment,
    removePendingComment,
    canComment,
    deleteComment
  } = usePostComments(post.id);

  const handleCommentClick = () => {
    if (!canComment) {
      window.location.href = '/?tab=signin';
      return;
    }
    setIsCommentsOpen(!isCommentsOpen);
  };

  const handleSubmitComment = async (content: string) => {
    setIsSubmitting(true);
    try {
      await addComment(content);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryComment = async (pendingComment: any) => {
    await retryComment(pendingComment);
  };

  // Navegação para detalhe do post
  const handlePostClick = () => {
    // Usar searchState da prop se disponível, senão usar location.state
    const currentSearchState = searchState || location.state?.searchState || {
      query: '',
      activeTab: 'all',
      results: []
    };

    navigate(`/post/${post.id}`, {
      state: { searchState: currentSearchState }
    });
  };

  // Exclusão de post
  const handleDeletePost = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_post_and_related', {
        post_uuid: post.id
      });
      if (error) throw error;
      setShowDeleteDialog(false);
      // Recarregar a página para atualizar os resultados
      window.location.reload();
    } catch (error) {
      toast({
        title: 'Failed to delete post',
        description: 'Failed to delete post. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-[#160C0C] rounded-2xl shadow-md py-5 mb-4 w-full max-w-2xl mx-auto relative">
      {/* Botão de 3 pontos (apenas para o autor) */}
      {isOwnPost && (
        <div className="absolute top-3 right-0 z-10">
          <CustomDropdownMenu
            trigger={
              <span className="ml-2 p-1 rounded-full focus:outline-none cursor-pointer transition-colors inline-flex items-center justify-center">
                <MoreHorizontal className="h-5 w-5 text-gray-400" />
              </span>
            }
            items={[{
              label: "Delete Post",
              icon: <Trash2 className="h-4 w-4 mr-2" />,
              onClick: () => setShowDeleteDialog(true),
              className: "text-white"
            }]}
          />
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="w-[80vw] max-w-xl bg-white rounded-xl p-8 shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-sans text-xl font-bold text-black">
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700 font-sans">
              After deleting this post, the action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 mt-6">
            <AlertDialogCancel
              disabled={deleting}
              className="rounded-full border border-black bg-white text-black font-sans px-6 py-2 hover:bg-gray-100 focus:!outline-none focus:!ring-0 focus:!ring-offset-0"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePost}
              disabled={deleting}
              className="rounded-full bg-sossiety-red-600 hover:bg-sossiety-red-700 text-white font-sans px-6 py-2 focus:!outline-none focus:!ring-0 focus:!ring-offset-0"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Author Info */}
      <div className="flex items-center space-x-3 mb-2">
        <Link to={post.author.username ? `/profile/${post.author.username}` : '#'} className="flex items-center space-x-3 group" tabIndex={post.author.username ? 0 : -1} aria-disabled={!post.author.username}>
          <div className="flex-shrink-0">
            <FramedAvatar
              src={post.author.avatar_url}
              alt={post.author.full_name}
              initials={post.author.full_name ? post.author.full_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : 'A'}
              frameSize={{ w: 32, h: 36 }}
              beltRank={post.author.belt_rank || 'white'}
              fontSize={11}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 min-w-0">
              <span className="font-semibold text-white text-sm truncate max-w-[60%] group-hover:underline">{post.author.full_name || 'Anonymous'}</span>
              <span className="text-xs text-gray-400 truncate max-w-[40%]">• {formatShortDistance(new Date(post.created_at))}</span>
            </div>
            <div className="text-xs text-gray-400 font-light">
              {post.author.academy_name || 'Search result'}
            </div>
          </div>
        </Link>
      </div>
      
      {/* Content - clicável para navegar para detalhe */}
      <div 
        className="mb-3 mt-1 cursor-pointer hover:bg-white/5 rounded-lg p-2 -m-2 transition-colors"
        onClick={handlePostClick}
      >
        <p className="text-gray-100 break-words post-content">{post.content}</p>
      </div>
      
      {/* Actions */}
      <div className="flex items-center space-x-8 pt-2 mt-2">
        <LikeButton 
          postId={post.id} 
          className="text-gray-200 hover:text-sossiety-red-600"
        />
        <CommentButton
          commentCount={commentCount}
          onClick={handleCommentClick}
          className="text-gray-200 hover:text-sossiety-red-600"
          disabled={!canComment}
          filled={isCommentsOpen}
        />
      </div>

      {/* Comments Section */}
      {isCommentsOpen && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          {/* Comment Input */}
          {canComment && (
            <div className="mb-4">
              <CommentInput
                onSubmit={handleSubmitComment}
                isSubmitting={isSubmitting}
              />
            </div>
          )}

          {/* Comments List */}
          <div className="space-y-2">
            {/* Pending Comments */}
            {pendingComments.map((pendingComment) => (
              <PendingCommentItem
                key={pendingComment.id}
                comment={pendingComment}
                onRetry={() => handleRetryComment(pendingComment)}
                onRemove={() => removePendingComment(pendingComment.id)}
              />
            ))}

            {/* Regular Comments */}
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onDelete={deleteComment}
              />
            ))}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}

            {/* Empty State */}
            {!isLoading && comments.length === 0 && pendingComments.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-400 text-sm">
                  This publication has no comments yet. Be the first to comment!
                </p>
              </div>
            )}

            {/* Load More Button */}
            {hasMore && !isLoading && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMoreComments}
                  className="border-white text-white bg-white/5 hover:bg-white/10 rounded-full px-6 font-semibold"
                >
                  View more comments
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 